// @flow
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Divider, Grid, Header, Icon, List, Message } from "semantic-ui-react";
import { Trans, useTranslation } from "react-i18next";
import { reduxForm, type FormProps, clearFields } from "redux-form";
import { Link } from "react-router-dom";
import { isEmpty } from "Commons/Utilities/helpers";

import { WechselFragenBaseRoute } from "modules/ContentPages/misc/ContentPageRoutes";
import { changeServiceBaseRoute, getStepRoute } from "../../../misc/Routes";
import Steps, { getSlug } from "modules/ChangeService/Wizard/misc/Steps";
import { Loading } from "Commons/Components/Loading/Loading";
import { CategoryIcon } from "Commons/Modules/category";
import { AuthDeterminedVisibility } from "Commons/Modules/auth/components/AuthDeterminedVisibility";
import { NOT_UNIQUE_COUNTERNUMBER } from "modules/ChangeService/Wizard/misc/Constants";

import "./Confirmation.scss";
import { withIsUserAuthenticated } from "../../../../../../Commons/Modules/auth/components/IsUserAuthenticated";
import { LoginOrRegister } from "Commons/Modules/auth/components/LoginOrRegister/LoginOrRegister";
import { BenefitsArrowsAndCircles, NotUniqueCounterNumber } from "../../../misc/Components";
import { useConfirmationStepHook } from "../../../misc/ConfirmationStepHook";
import { MODES } from "Commons/Modules/auth/components/LoginOrRegister/misc/modes";
import { signUpLoginPage } from "Commons/Utilities/GoogleTagManager";
import { gaswechselUrl, stromwechselUrl } from "modules/MyShortOffers/misc/Routes";
import DashboardRoute from "modules/Dashboard/misc/Routes";
import { scrollToPosition } from "Commons/Utilities/scrollTo";

import { getSingleChangeService, updateChangeService } from "modules/ChangeService/misc/Api";

type Props = {|
  categorySlug: string,
|} & FormProps;

function ConfirmationStep(props: Props) {
  const { reset, change, categorySlug, handleOnAuthenticated } = props;
  const dispatch = useDispatch();
  const { t, isCreating, creationError, creationSuccess } = useConfirmationStepHook(categorySlug);

  if (!!creationError || creationSuccess) {
    return (
      <State
        creationError={creationError}
        creationSuccess={creationSuccess}
        categorySlug={categorySlug}
        reset={reset}
        change={change}
        dispatch={dispatch}
      />
    );
  }

  if (isCreating) {
    return <Loading />;
  }

  return (
    <NewRegister flags={{ regWsFlag: true }} memoizedHandleAuthenticate={handleOnAuthenticated} t={t} buttonWidth={8} />
  );
}

type StateProps = {|
  creationSuccess: boolean,
  creationError: boolean | string,
  categorySlug: string,
  reset: $PropertyType<FormProps, "reset">,
  change: $PropertyType<FormProps, "change">,
  dispatch: Function,
|};

const SuccessMessage = ({ categoryName, t }: { categoryName: string, t: typeof TFunction }) => {
  return (
    <Grid.Column width="11">
      <AuthDeterminedVisibility authOnly={false}>
        <Divider hidden />
        <Header as="h3" className={"changeservice-themed"}>
          <Trans t={t} i18nKey="wizard.confirmationPage.newUser.creationSuccessTitle" values={{ categoryName }}>
            Text
            <p>Text</p>
          </Trans>
        </Header>
        <Divider hidden />
        <Trans t={t} i18nKey="wizard.confirmationPage.newUser.description" values={{ category: categoryName }}>
          <p>Text</p>
          <div>
            Text
            <br />
            Text
          </div>
        </Trans>
      </AuthDeterminedVisibility>
      <AuthDeterminedVisibility authOnly={true}>
        <Header as="h3" className={"changeservice-themed"}>
          <Trans t={t} i18nKey="wizard.confirmationPage.creationSuccess" values={{ categoryName }}>
            Text
            <p>Text</p>
          </Trans>
        </Header>
      </AuthDeterminedVisibility>
    </Grid.Column>
  );
};

function State(props: StateProps) {
  const { reset, change, categorySlug, creationError, creationSuccess, dispatch } = props;
  const [yesOrNo, setYesOrNo] = useState("");
  const { t } = useTranslation("changeService");
  const categoryName = categorySlug === "gas" ? "Gas" : "Strom";
  const invertedCategoryName = categorySlug !== "gas" ? "Gas" : "Strom";
  const createdCS = useSelector(({ changeServices }) => changeServices.newlyCreatedChangeService);
  const user = useSelector(({ user }) => user.user);
  const sofLink = categorySlug === "strom" ? gaswechselUrl : stromwechselUrl;

  const handleYesOrNo = (choice) => {
    setYesOrNo(choice);
    const secondaryEnergyTypeExists = { secondaryEnergyTypeExists: choice === "yes" ? true : false };
    !isEmpty(createdCS) && updateCreatedChangeService(secondaryEnergyTypeExists);
  };

  /**
  * Updates the newly created CS with the a new property (secondaryEnergyTypeExists).
    We get the change service from the back end because it must include the "version" property which is set server-side and not from the form.
  * @param {boolean} secondaryEnergyTypeExists: if the user has the other type of energy
  */
  const updateCreatedChangeService = async (secondaryEnergyTypeExists) => {
    const data = await getSingleChangeService(user.id, createdCS.id);
    const createdCSFromServer = data[createdCS.categoryId][0];
    updateChangeService(createdCSFromServer.userId, { ...createdCSFromServer, ...secondaryEnergyTypeExists });
  };

  useEffect(() => {
    return () => {
      if (!creationError) {
        dispatch(
          clearFields(
            "ChangeService",
            false,
            false,
            "currentConsumption",
            "categoryProviderId",
            "currentTariff",
            "customerNumber",
            "counterNumber",
            "comment"
          )
        );
      }
    };
  }, [reset, change, creationError, dispatch]);

  const StromSection = () => (
    <>
      {!yesOrNo && (
        <>
          <Grid.Row className="benefits" />
          <Grid.Row className="benefits">
            <Grid.Column width={4} />
            <Grid.Column width={2} verticalAlign={"middle"}>
              <CategoryIcon.Icon
                className={"white-icon"}
                size={"huge"}
                name={categorySlug === "gas" ? "strom" : "gas"}
              />
            </Grid.Column>
            <Grid.Column verticalAlign={"middle"} width={6}>
              <Header as="h2" className={"title"}>
                <Header.Content>
                  {t("wizard.confirmationPage.question", { categoryName: invertedCategoryName })}
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={4} />
          </Grid.Row>
          <Grid.Row className="benefits">
            <Grid.Column width={4} />
            <Grid.Column width={4} textAlign={"center"}>
              <Button color="black" size="big" onClick={() => handleYesOrNo("yes")}>
                {t("common:yes")}
                <Icon name="arrow right" />
              </Button>
            </Grid.Column>
            <Grid.Column width={4} textAlign={"center"}>
              <Button color="black" size="big" onClick={() => handleYesOrNo("no")}>
                {t("common:no")}
                <Icon name="arrow right" />
              </Button>
            </Grid.Column>
            <Grid.Column width={4} />
          </Grid.Row>
        </>
      )}
      {yesOrNo === "yes" && (
        <Grid.Row className="benefits">
          <Divider hidden />
          <Grid.Column width={2} />
          <Grid.Column width={6}>
            <Divider hidden />
            <List>
              {t("mobile.confirmation.benefits.list", { returnObjects: true }).map((benefit, i) => {
                return (
                  <List.Item key={`benefist-li${i}`}>
                    <List.Icon size="large" name="check circle" className={"white-icon"} />
                    <List.Content>{benefit}</List.Content>
                    <Divider hidden key={`div${i}`} />
                  </List.Item>
                );
              })}
            </List>
          </Grid.Column>
          <Grid.Column width={6} verticalAlign={"middle"}>
            <div>
              <Link to={sofLink}>
                <Button color="black" size="big" className="confirmationCSLink">
                  {t("wizard.confirmationPage.startNewCS", { categoryName: invertedCategoryName })}
                  <Icon name="arrow right" />
                </Button>
              </Link>
              <Link to={getStepRoute(false, categorySlug === "gas" ? "strom" : "gas", getSlug(Steps.CONTRACT))}>
                <Button color="black" size="big" className="confirmationCSLink">
                  {t("wizard.confirmationPage.activateNewCS", { categoryName: invertedCategoryName })}
                  <Icon name="arrow right" />
                </Button>
              </Link>
            </div>
          </Grid.Column>
          <Grid.Column width={2} />
        </Grid.Row>
      )}
      {yesOrNo === "no" && (
        <Grid.Row className={"benefits"}>
          <Grid.Column width={4} />
          <Grid.Column width={8} textAlign={"center"}>
            <Header as="h2" className={"title"}>
              <Header.Content>
                Vielen Dank für ihre Antwort.
                <Link to={DashboardRoute}>
                  <Button color="black" size="big" className="confirmationCSLink">
                    {t("wizard.backToOverview")}
                    <Icon name="arrow right" />
                  </Button>
                </Link>
              </Header.Content>
            </Header>
          </Grid.Column>
          <Grid.Column width={4} />
        </Grid.Row>
      )}
    </>
  );

  const GasSection = () => (
    <>
      <Grid.Row className={"benefits"} />
      <Grid.Row className={"benefits"}>
        <Grid.Column width={4} />
        <Grid.Column width={2} verticalAlign={"middle"}>
          <CategoryIcon.Icon className={"white-icon"} size={"huge"} name={categorySlug === "gas" ? "strom" : "gas"} />
        </Grid.Column>
        <Grid.Column verticalAlign={"middle"} width={6}>
          <Header as="h2" className={"title"}>
            <Header.Content>
              {t("mobile.confirmation.benefits.title", { categoryName: invertedCategoryName })}
            </Header.Content>
          </Header>
        </Grid.Column>
        <Grid.Column width={4} />
      </Grid.Row>
      <Grid.Row className={"benefits"}>
        <Divider hidden />
        <Grid.Column width={2} />
        <Grid.Column width={6}>
          <Divider hidden />
          <List>
            {t("mobile.confirmation.benefits.list", { returnObjects: true }).map((benefit, i) => {
              return (
                <List.Item key={`benefist-li${i}`}>
                  <List.Icon size="large" name="check circle" className={"white-icon"} />
                  <List.Content>{benefit}</List.Content>
                  <Divider hidden key={`div${i}`} />
                </List.Item>
              );
            })}
          </List>
        </Grid.Column>
        <Grid.Column width={6} verticalAlign={"middle"}>
          <div>
            <Link to={sofLink}>
              <Button color="black" size="big" className="confirmationCSLink">
                {t("wizard.confirmationPage.startNewCS", { categoryName: invertedCategoryName })}
                <Icon name="arrow right" />
              </Button>
            </Link>
            <Link to={getStepRoute(false, categorySlug === "gas" ? "strom" : "gas", getSlug(Steps.CONTRACT))}>
              <Button color="black" size="big" className="confirmationCSLink">
                {t("wizard.confirmationPage.activateNewCS", { categoryName: invertedCategoryName })}
                <Icon name="arrow right" />
              </Button>
            </Link>
          </div>
        </Grid.Column>
        <Grid.Column width={2} />
      </Grid.Row>
    </>
  );

  if (creationSuccess) {
    return (
      <Grid className="Confirmation-Desktop">
        <Grid.Row>
          <Grid.Column width="2" />
          <Grid.Column width="1" verticalAlign="middle">
            <Icon name="check circle" size="large" color="green" />
          </Grid.Column>
          <SuccessMessage categoryName={categoryName} invertedCategoryName={invertedCategoryName} t={t} />
          <Grid.Column width={2} />
        </Grid.Row>
        <Divider hidden />
        {categorySlug === "strom" ? StromSection() : GasSection()}
        <Grid.Row>
          <Grid.Column width={2} />
          <Grid.Column width={12} textAlign={"center"}>
            <BenefitsArrowsAndCircles t={t} invertedCategoryName={categoryName} />
            <Link to={WechselFragenBaseRoute} onClick={() => scrollToPosition(0, 0)}>
              <Button primary size="big">
                {t("wizard.confirmation.button")}
              </Button>
            </Link>
          </Grid.Column>
          <Grid.Column width={2} />
        </Grid.Row>
      </Grid>
    );
  }

  if (creationError.toString().includes("400")) {
    return (
      <div>
        <Message
          info
          header={t("wizard.messages.alreadyExistingHeader")}
          content={t("wizard.messages.alreadyExistingMessage")}
        />
        <Trans t={t} i18nKey={"wizard.messages.alreadyExistingSubmessage"}>
          <div>
            <Link to={changeServiceBaseRoute}>Sehen sie hier Ihre aktuellen Wechselservices</Link>
          </div>
          Dort können Sie Ihren Wechselservice auch bearbeiten.
        </Trans>
      </div>
    );
  }

  if (creationError === NOT_UNIQUE_COUNTERNUMBER) {
    return <NotUniqueCounterNumber change={change} categorySlug={categorySlug} />;
  }
  return <Message negative header={t("wizard.messages.failureHeader")} content={t("wizard.messages.failureMessage")} />;
}

const Confirmation = reduxForm({
  form: "ChangeService",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ConfirmationStep);

export { Confirmation };

type NewRegisterProps = {
  memoizedHandleAuthenticate: Function,
  t: typeof TFunction,
  flags: Dict,
  isUserAuthenticated?: boolean,
  userId?: string,
};

export let NewRegister = ({ isUserAuthenticated, memoizedHandleAuthenticate, t, flags, userId }: NewRegisterProps) => {
  if (isUserAuthenticated && memoizedHandleAuthenticate) {
    memoizedHandleAuthenticate(userId);
  } else {
    signUpLoginPage("ChangeServiceWizard - Desktop");
  }

  return (
    <>
      <Divider hidden />
      <Divider hidden />
      <Grid>
        <Grid.Column width={2} />
        <Grid.Column width={12}>
          <LoginOrRegister
            flags={{ regWsFlag: true }}
            onAuthenticate={memoizedHandleAuthenticate}
            submitButtonText={t("wizard.customer.submitButton")}
            header={{
              funnel: (
                <Grid>
                  <Grid.Column textAlign="center">
                    <Grid.Row>
                      <Header as={"h2"}>{t("wizard.confirmation.registration.header")} </Header>
                    </Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                    <Grid.Row>
                      <Header as={"h3"}>{t("wizard.confirmation.registration.funnel.subTitle")}</Header>
                    </Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                  </Grid.Column>
                </Grid>
              ),
              register: (
                <Grid>
                  <Grid.Column textAlign="center">
                    <Grid.Row>
                      <Header as={"h2"}>{t("wizard.confirmation.registration.header")} </Header>
                    </Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                    <Grid.Row>{t("wizard.confirmation.registration.register.subTitle")}</Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                    <Grid.Row>
                      <Header as={"h3"}>{t("wizard.confirmation.registration.register.title")}</Header>
                    </Grid.Row>
                    <Divider hidden />
                  </Grid.Column>
                </Grid>
              ),
              login: (
                <Grid>
                  <Grid.Column textAlign="center">
                    <Grid.Row>
                      <Header as={"h2"}>{t("wizard.confirmation.registration.header")} </Header>
                    </Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                    <Grid.Row>{t("wizard.confirmation.registration.login.subTitle")}</Grid.Row>
                    <Divider hidden />
                    <Divider hidden />
                    <Grid.Row>
                      <Header as={"h3"}>{t("wizard.confirmation.registration.login.title")}</Header>
                    </Grid.Row>
                    <Divider hidden />
                  </Grid.Column>
                </Grid>
              ),
            }}
            mode={MODES.FUNNEL}
            basic
          />
        </Grid.Column>
        <Grid.Column width={2} />
      </Grid>
    </>
  );
};

NewRegister = withIsUserAuthenticated(NewRegister);
